import React from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, UserCircle, LogOut, Settings } from 'lucide-react';
import { AdminDmBadge } from './AdminDmBadge';
import { AdminClientDmBadge } from './AdminClientDmBadge';

/**
 * GlobalNav — Persistent mini navigation bar on ALL authenticated pages.
 * Provides a reliable "go home" link that never disappears.
 */
const GlobalNav = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  if (!user) return children;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const homeRoutes = {
    admin: '/admin/home',
    super_admin: '/admin/home',
    talent: '/talent/home',
    client: '/client/home',
    agency: '/agency/dashboard',
  };

  const homeRoute = homeRoutes[user?.role] || '/';

  // Don't show on home pages themselves (avoids duplication)
  const isHomePage = typeof window !== 'undefined' && (
    window.location.pathname === homeRoute ||
    window.location.pathname === '/admin/home' ||
    window.location.pathname === '/talent/home' ||
    window.location.pathname === '/client/home'
  );

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Settings hub per role (houses the Appearance / theme control).
  const settingsRoutes = {
    talent: '/talent/settings',
    client: '/client/settings',
    agency: '/agency/settings',
    admin: '/admin/settings',
    super_admin: '/admin/settings',
  };
  const settingsRoute = settingsRoutes[user?.role];

  return (
    <>
      {!isHomePage && (
        <div className="bg-gray-900 px-4 py-2.5 flex items-center gap-3 sticky top-0 z-50" data-testid="global-nav">
          <button
            onClick={() => navigate(homeRoute)}
            className="flex items-center gap-2 bg-[#03DFFC]/10 hover:bg-[#03DFFC]/20 px-3 py-1.5 rounded-lg transition-all"
            data-testid="global-home-link"
          >
            <Home className="w-5 h-5 text-[#03DFFC]" />
            <span className="text-sm font-bold text-[#03DFFC]">Home</span>
          </button>
          <span className="text-gray-600">|</span>
          <span className="text-xs text-gray-400 capitalize">{user?.role} Dashboard</span>
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && <AdminDmBadge variant="nav" />}
            {isAdmin && <AdminClientDmBadge variant="nav" />}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/profile')}
                className="flex items-center gap-2 bg-[#03DFFC]/10 hover:bg-[#03DFFC]/20 px-3 py-1.5 rounded-lg transition-all"
                data-testid="global-profile-link"
              >
                <UserCircle className="w-5 h-5 text-[#03DFFC]" />
                <span className="hidden sm:inline text-sm font-bold text-[#03DFFC]">My Profile</span>
              </button>
            )}
            {/* Iter-233 post-deploy feedback — Logout always visible (desktop + mobile). */}
            {settingsRoute && (
              <button
                onClick={() => navigate(settingsRoute)}
                className="flex items-center gap-2 bg-[#03DFFC]/10 hover:bg-[#03DFFC]/20 px-3 py-1.5 rounded-lg transition-all"
                data-testid="global-settings-link"
                title="Settings & Appearance"
              >
                <Settings className="w-5 h-5 text-[#03DFFC]" />
                <span className="hidden sm:inline text-sm font-bold text-[#03DFFC]">Settings</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 px-3 py-1.5 rounded-lg transition-all"
              data-testid="global-logout-btn"
              title="Logout"
            >
              <LogOut className="w-5 h-5 text-rose-400" />
              <span className="hidden sm:inline text-sm font-bold text-rose-400">Logout</span>
            </button>
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default GlobalNav;
